// Off-chain metadata cache for payment requests (Get Paid section).
//
// The on-chain `PaymentRequest` and `LockedPaymentRequest` structs only carry
// the financial fields (requestId, requester, receiver, tokenAddress, amount,
// isAccepted, milestones, etc.) — there is no slot for human-readable text or
// a creation timestamp.  To keep the UI showing the description / date the
// user typed when they created the request, we cache a small meta object
// per chain + sender + requestId.
//
// Each entry is `{ description?: string, createdAt?: number }`.
//
// This is intentionally local-first; replace with a backend record if you
// ever decide to share descriptions across devices.

const STORAGE_KEY = 'cronpay_payment_request_notes';

const safeRead = () => {
    try {
        const raw =
            typeof localStorage !== 'undefined'
                ? localStorage.getItem(STORAGE_KEY)
                : null;
        return raw ? JSON.parse(raw) : {};
    } catch (_e) {
        return {};
    }
};

const safeWrite = (data) => {
    try {
        if (typeof localStorage !== 'undefined') {
            localStorage.setItem(STORAGE_KEY, JSON.stringify(data));
        }
    } catch (_e) {
        // localStorage may be unavailable (private mode, quota exceeded, etc.)
    }
};

const buildKey = ({ chainId, sender, requestId, kind }) => {
    const safeSender = String(sender || '').toLowerCase();
    const safeKind = kind || 'direct';
    return `${chainId || 'na'}:${safeKind}:${safeSender}:${requestId}`;
};

const normalizeEntry = (raw) => {
    if (!raw) return null;
    if (typeof raw === 'string') return { description: raw };
    if (typeof raw === 'object') return raw;
    return null;
};

export const setPaymentRequestMeta = ({
    chainId,
    sender,
    requestId,
    kind,
    description,
    createdAt,
}) => {
    if (requestId === undefined || requestId === null || requestId === '') return;
    const map = safeRead();
    const key = buildKey({ chainId, sender, requestId, kind });
    const existing = normalizeEntry(map[key]) || {};
    map[key] = {
        ...existing,
        ...(description !== undefined ? { description } : {}),
        ...(createdAt !== undefined ? { createdAt } : {}),
    };
    safeWrite(map);
};

const normalizeRequestKind = (kind) => (kind === 'tracked' ? 'milestone' : kind || 'direct');

export const getPaymentRequestMeta = ({ chainId, sender, requestId, kind }) => {
    if (requestId === undefined || requestId === null || requestId === '') {
        return {};
    }
    const map = safeRead();
    return normalizeEntry(map[buildKey({ chainId, sender, requestId, kind })]) || {};
};

// -------- Backward compatible aliases (description-only) --------

export const setPaymentRequestNote = ({
    chainId,
    sender,
    requestId,
    kind,
    description,
}) => {
    setPaymentRequestMeta({ chainId, sender, requestId, kind, description });
};

export const getPaymentRequestNote = ({ chainId, sender, requestId, kind }) => {
    return (
        getPaymentRequestMeta({ chainId, sender, requestId, kind }).description || ''
    );
};

/** Meta for an incoming request (lookup by requester wallet + on-chain id). */
export const getIncomingPaymentRequestMeta = ({
    chainId,
    requester,
    requestId,
    kind,
}) =>
    getPaymentRequestMeta({
        chainId,
        sender: requester,
        requestId,
        kind: normalizeRequestKind(kind),
    });

const CHAIN_INDEX_SORT_BASE = 1e11;

/** Numeric key for newest-first sorting (higher = more recent). */
export const getPaymentRequestSortKey = ({
    chainId,
    sender,
    requester,
    requestId,
    kind,
    chainIndex,
}) => {
    const id = Number(requestId);
    const meta = requester
        ? getIncomingPaymentRequestMeta({ chainId, requester, requestId: id, kind })
        : getPaymentRequestMeta({
              chainId,
              sender,
              requestId: id,
              kind: normalizeRequestKind(kind),
          });

    const createdAt = Number(meta.createdAt || 0);
    if (createdAt > 0) return createdAt;
    if (id > 0) return id;
    if (chainIndex != null && !Number.isNaN(Number(chainIndex))) {
        return CHAIN_INDEX_SORT_BASE + Number(chainIndex);
    }
    return 0;
};

export const sortPaymentRequestsNewestFirst = (requests, sortContext = {}) =>
    [...requests].sort((a, b) => {
        const aKey = getPaymentRequestSortKey({
            ...sortContext,
            requestId: a.id ?? a.requestId,
            requester: a.requester,
            kind: a.kind ?? sortContext.kind,
            chainIndex: a.chainIndex,
        });
        const bKey = getPaymentRequestSortKey({
            ...sortContext,
            requestId: b.id ?? b.requestId,
            requester: b.requester,
            kind: b.kind ?? sortContext.kind,
            chainIndex: b.chainIndex,
        });
        return bKey - aKey;
    });
